/**
 * First-run onboarding checklist card (Phase 4 Action 46).
 *
 * Renders above the admin dashboard when the checklist is enabled AND
 * has at least one unfinished item. Self-hides when all items are
 * complete OR the tenant has the flag turned off.
 *
 * Each item exposes:
 *   - Title + description
 *   - "Open" deep-link CTA to the relevant Command Center page
 *   - "Mark done" checkbox that POSTs to /admin/onboarding/checklist/complete
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_ORIGIN } from '../api/baseUrl';
import { loadTokens } from '../api/adminClient';

interface ChecklistItem {
  key: string;
  title: string;
  description: string;
  cta?: { label: string; path: string };
  completed: boolean;
  completedAt: string | null;
}

interface ChecklistSnapshot {
  enabled: boolean;
  items: ChecklistItem[];
  completedCount: number;
  totalCount: number;
  allComplete: boolean;
}

async function fetchWithToken<T>(path: string, init?: RequestInit): Promise<T> {
  loadTokens();
  const token = localStorage.getItem('admin_access_token');
  const res = await fetch(`${API_ORIGIN}/api/admin${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) throw new Error(`${path} → ${res.status}`);
  return res.json();
}

export default function OnboardingChecklist() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['onboarding', 'checklist'],
    queryFn: () => fetchWithToken<{ data: ChecklistSnapshot }>('/onboarding/checklist'),
  });

  const complete = useMutation({
    mutationFn: (itemKey: string) =>
      fetchWithToken<{ data: { snapshot: ChecklistSnapshot; checklist_just_completed: boolean } }>(
        '/onboarding/checklist/complete',
        { method: 'POST', body: JSON.stringify({ item_key: itemKey }) },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'checklist'] });
    },
  });

  if (isLoading || !data) return null;
  const snapshot = data.data;
  if (!snapshot.enabled || snapshot.allComplete) return null;

  const percent = Math.round((snapshot.completedCount / snapshot.totalCount) * 100);

  return (
    <div className="mb-8 rounded-lg border border-blue-200 bg-blue-50 p-6">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-blue-900">Finish setting up PowerMove</h2>
          <p className="mt-1 text-sm text-blue-800">
            Complete these steps so your team gets the most out of PowerMove from day one.
          </p>
        </div>
        <div className="text-right text-sm text-blue-900">
          <div className="font-semibold">{snapshot.completedCount} / {snapshot.totalCount} done</div>
          <div className="mt-1 h-1.5 w-32 rounded-full bg-blue-200">
            <div
              className="h-1.5 rounded-full bg-blue-600"
              style={{ width: `${percent}%` }}
            />
          </div>
        </div>
      </div>

      <ul className="space-y-3">
        {snapshot.items.map((item) => (
          <li
            key={item.key}
            className="flex items-start gap-3 rounded-md border border-blue-200 bg-white p-3"
          >
            <input
              type="checkbox"
              checked={item.completed}
              disabled={item.completed || complete.isPending}
              onChange={() => complete.mutate(item.key)}
              className="mt-1 h-4 w-4 rounded border-blue-300 text-blue-600 focus:ring-blue-500 disabled:opacity-60"
            />
            <div className="flex-1">
              <div className={`text-sm font-medium ${item.completed ? 'text-slate-500 line-through' : 'text-slate-900'}`}>
                {item.title}
              </div>
              <div className="mt-0.5 text-xs text-slate-600">{item.description}</div>
            </div>
            {!item.completed && item.cta && (
              <a
                href={item.cta.path}
                target={item.cta.path.startsWith('http') ? '_blank' : undefined}
                rel="noreferrer"
                className="rounded-md border border-slate-300 bg-white px-3 py-1 text-xs text-slate-700 hover:bg-slate-50"
              >
                {item.cta.label}
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
